import React, { useState, useEffect } from 'react';
import { MdVisibility, MdDelete } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, collection, onSnapshot, deleteDoc } from 'firebase/firestore';
import { db as firestoreDb } from '../../firebase'; // Adjust path as necessary
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import '../styles/Dashboard.css';

// Register Chart.js elements
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const Dashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);

  // Fetch orders and products from Firestore
  useEffect(() => {
    const unsubscribeOrders = onSnapshot(collection(firestoreDb, 'orders'), (snapshot) => {
      const orderList = snapshot.docs.map((doc) => {
        const data = doc.data();
        const createdAt = data.createdAt?.seconds
          ? new Date(data.createdAt.seconds * 1000).toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          })
          : 'Unknown Date';

        return {
          id: doc.id,
          name: data.name || data.shipping?.name || data.user?.name || 'N/A',
          amount: data.total || 0,
          status: data.status || 'N/A',
          dateFormatted: createdAt,
          items: data.items || [],
          paymentMethod: data.paymentMethod || 'N/A',
        };
      });
      setOrders(orderList);
    });

    const unsubscribeProducts = onSnapshot(collection(firestoreDb, 'products'), (snapshot) => {
      const fetchedProducts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProducts(fetchedProducts);
    });

    return () => {
      unsubscribeOrders();
      unsubscribeProducts();
    };
  }, []);

  // Calculate the total revenue and unique buyers
  const totalRevenue = orders.reduce((acc, order) => acc + (order.amount || 0), 0);
  const uniqueBuyers = new Set(orders.map((order) => order.name)).size;

  // Calculate the total quantity sold for each product
  const productSales = products.map(product => {
    const soldQuantity = orders.reduce((total, order) => {
      const productInOrder = order.items && Array.isArray(order.items)
        ? order.items.find(item => item.productId === product.id)
        : null;
      return total + (productInOrder ? productInOrder.qty : 0);
    }, 0);
    return {
      name: product.name,
      sold: soldQuantity,
    };
  });

  // Bullet Chart Data for Products Sold
  const productsChartData = {
    labels: productSales.map(product => product.name),
    datasets: [
      {
        label: 'Products Sold',
        data: productSales.map(product => product.sold),
        backgroundColor: 'rgba(75, 192, 192, 0.7)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
    ],
  };

  // Doughnut Chart Data for Revenue with dynamic customization
  const revenueChartData = {
    labels: ['Revenue', 'Remaining'],
    datasets: [
      {
        data: [totalRevenue, 1000 - totalRevenue], // Adjust remaining dynamically
        backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(255, 99, 132, 0.6)'],
        borderWidth: 0, // Optionally remove borders
      },
    ],
  };

  // Doughnut Chart Options
  const revenueChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 14,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => {
            const value = tooltipItem.raw;
            return `₹${value.toFixed(2)}`; // Formatting tooltip value
          },
        },
      },
    },
  };

  // Status color mapping for orders
  const getStatusColor = (status) => {
    switch (status) {
      case 'Placed':
        return 'gray';
      case 'Processing':
        return 'blue';
      case 'Shipped':
        return 'green';
      case 'Delivered':
        return 'purple';
      case 'Cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Navigate to order details page
  const handleView = (orderId) => {
    navigate(`/admin/orders/${orderId}`);
  };

  // Delete order from Firestore
  const handleDelete = async (orderId) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        await deleteDoc(doc(firestoreDb, 'orders', orderId));
      } catch (error) {
        console.error('Error deleting order:', error);
      }
    }
  };

  // Update order status in Firestore
  const handleStatusChange = async (orderId, newStatus) => {
    if (!orderId || !newStatus) return;
    try {
      const orderRef = doc(firestoreDb, 'orders', orderId);
      await updateDoc(orderRef, { status: newStatus });
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  return (
    <div className="dashboard-container">
      <h2 className="dashboard-title">Dashboard</h2>

      {/* Top Cards */}
      <div className="dashboard-cards">
        <div className="card">
          Total Orders<br /><strong>{orders.length}</strong>
        </div>
        <div className="card">
          Total Products<br /><strong>{products.length}</strong>
        </div>
        <div className="card">
          Total Buyers<br /><strong>{uniqueBuyers}</strong>
        </div>
        <div className="card">
          Total Revenue<br /><strong>₹{totalRevenue}</strong>
        </div>
      </div>


      <div className="dashboard-charts">

        <div className="chart-box">
          <h4 style={{ marginBottom: '1rem', textAlign: 'left' }}>Revenue Chart</h4>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            {/* Custom Legend on the left */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginRight: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ width: '12px', height: '12px', backgroundColor: 'rgba(75, 192, 192, 0.6)', marginRight: '8px' }} />
                <span style={{ fontSize: '14px' }}>Revenue</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '12px', height: '12px', backgroundColor: 'rgba(255, 99, 132, 0.6)', marginRight: '8px' }} />
                <span style={{ fontSize: '14px' }}>Remaining</span>
              </div>
            </div>

            <div style={{ width: '160px', height: '200px' }}>
              <Doughnut
                data={revenueChartData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { display: false },
                    tooltip: { enabled: true },
                  },
                }}
              />
            </div>
          </div>
        </div>



        <div className="chart-box">
          <h4>Products Sold</h4>
          <Bar
            data={productsChartData}
            options={{
              indexAxis: 'y',
              responsive: true,
              plugins: {
                legend: { display: false },
                tooltip: { enabled: true },
              },
              scales: {
                x: {
                  beginAtZero: true,
                  max: Math.max(...productSales.map(product => product.sold)) + 10, // dynamic max value based on data
                  grid: { color: 'rgba(200, 200, 200, 0.2)' },
                },
                y: {
                  grid: { display: false },
                },
              },
              animation: {
                duration: 1500,
                easing: 'easeOutBounce',
              },
            }}
          />
        </div>
      </div>

      {/* Latest Orders Table */}
      <div className="latest-orders">
        <h3>Latest Orders</h3>
        <div className="table-responsive">
          <table className="order-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer Name</th>
                <th>Products</th>
                <th>Amount (₹)</th>
                <th>Status</th>
                <th>Date</th>
                <th>Payment Method</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length > 0 ? (
                orders.map((order) => (
                  <tr key={order.id}>
                    <td>{order.id}</td>
                    <td>{order.name}</td>
                    <td>{order.items.map(item => `${item.name} (x${item.qty})`).join(', ')}</td>
                    <td>{order.amount}</td>
                    <td>
                      <span
                        className="order-status"
                        style={{
                          backgroundColor: getStatusColor(order.status),
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                        }}
                      >
                        {order.status}
                      </span>
                    </td>
                    <td>{order.dateFormatted}</td>
                    <td>{order.paymentMethod}</td>
                    <td>
                      <button className="view-btn" onClick={() => handleView(order.id)} title="View Order">
                        <MdVisibility />
                      </button>
                      <button className="delete-btn" onClick={() => handleDelete(order.id)} title="Delete Order">
                        <MdDelete />
                      </button>
                      <select
                        value={order.status || 'Placed'}
                        onChange={(e) => handleStatusChange(order.id, e.target.value)}
                        className="status-dropdown"
                      >
                        <option value="Placed">Placed</option>
                        <option value="Processing">Processing</option>
                        <option value="Shipped">Shipped</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '1rem' }}>
                    No orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
